/**
 * SoleadLab 2.0 — Detection Store ("Find Similar Faults" P2a)
 *
 * Zustand store for the map-first fault-detection review flow. Holds the
 * single in-flight/reviewed detection job, its ghost candidates, and the
 * score threshold that gates ghost visibility.
 *
 * Contract notes (frozen across the P2a packages):
 *   - refreshJob() is one poll tick (driven by useDetectionPoller). The
 *     store only flips job.status to 'complete' AFTER the result JSON has
 *     been fetched and mapped — a transient result-fetch failure leaves the
 *     job 'running' locally so the next tick retries; a 404 (job/result
 *     gone) flips it to 'error' so the poller stops.
 *   - dismiss() is best-effort: labels POST + job DELETE failures are
 *     logged, never thrown — local state is always cleared.
 *   - Candidate ids are "cand_<index>" — stable within one result, and the
 *     ids the labels endpoint receives back.
 */

import { create } from 'zustand';
import {
  createDetection,
  getDetectionJob,
  getDetectionResult,
  postDetectionLabels,
  cancelDetection,
} from '../services/detectionApi';
import type {
  DetectionScanMode,
  DetectionJobStatus,
  DetectionCandidateProps,
} from '../services/detectionApi';

// ─── Types ───────────────────────────────────────────────────────────

export interface DetectionCandidate {
  /** "cand_<index>" — stable within a result. */
  id: string;
  /** Closed 5-point lng/lat ring from the result. */
  ring: [number, number][];
  props: DetectionCandidateProps;
  state: 'ghost' | 'accepted' | 'rejected';
  /** Set on accept. */
  acceptedIndicationId?: string;
}

export interface DetectionStore {
  job: {
    id: string;
    siteId: string;
    orthoLayerId: string;
    tilesetId: string;
    scanMode: DetectionScanMode;
    status: DetectionJobStatus;
    error?: string;
    candidateCount?: number;
  } | null;
  candidates: DetectionCandidate[];
  /** score01 floor for ghost visibility, default 0.5. */
  threshold: number;
  /** Result summary verbatim. */
  summary: Record<string, unknown> | null;
  // actions
  startScan(
    siteId: string,
    orthoLayerId: string,
    tilesetId: string,
    scanMode: DetectionScanMode,
    indicationType?: string,
    exemplarIndicationIds?: string[]
  ): Promise<void>;
  /** One poll tick: GET job; on complete → fetch result, populate candidates. */
  refreshJob(): Promise<void>;
  setThreshold(v: number): void;
  markAccepted(candId: string, indicationId: string): void;
  markRejected(candId: string): void;
  unmarkRejected(candId: string): void;
  /** POST labels {acceptedIds, rejectedIds} then DELETE job; clear state.
   *  Never throws (best-effort labels + cancel). */
  dismiss(): Promise<void>;
  /** Local reset only. */
  clear(): void;
}

/** Internal-only fields — consumers code against DetectionStore. */
interface DetectionStoreInternal extends DetectionStore {
  /** Overlap guard: poll ticks can outpace a slow GET. */
  _refreshInFlight: boolean;
}

const DEFAULT_THRESHOLD = 0.5;
/** How many ghosts a fresh result shows by default (the review sweet spot —
 *  the gate-proven triage depth). The slider can always reveal the rest. */
const DEFAULT_VISIBLE_GHOSTS = 200;

function isTerminal(status: DetectionJobStatus): boolean {
  return status !== 'pending' && status !== 'running';
}

function is404(err: unknown): boolean {
  return (
    err instanceof Error &&
    (err as Error & { status?: number }).status === 404
  );
}

// ─── Store ───────────────────────────────────────────────────────────

export const useDetectionStore = create<DetectionStoreInternal>((set, get) => ({
  job: null,
  candidates: [],
  threshold: DEFAULT_THRESHOLD,
  summary: null,
  _refreshInFlight: false,

  startScan: async (
    siteId,
    orthoLayerId,
    tilesetId,
    scanMode,
    indicationType,
    exemplarIndicationIds
  ) => {
    // Errors (409 scan-in-progress, 400 exemplar sync race, …) propagate to
    // the caller — the UI owns surfacing them. State is only touched on
    // success so a failed start never clobbers an existing review.
    const created = await createDetection(siteId, {
      scanMode,
      tilesetId,
      orthoLayerId,
      // undefined keys are dropped by JSON.stringify — anomaly scans send
      // neither field (the server rejects them if present).
      indicationType,
      exemplarIndicationIds,
    });
    set({
      job: {
        id: created.jobId,
        siteId,
        orthoLayerId,
        tilesetId,
        scanMode,
        status: created.status,
      },
      candidates: [],
      summary: null,
      _refreshInFlight: false,
    });
  },

  refreshJob: async () => {
    const { job, _refreshInFlight } = get();
    if (!job || _refreshInFlight || isTerminal(job.status)) return;
    set({ _refreshInFlight: true });
    try {
      const info = await getDetectionJob(job.siteId, job.id);
      const cur = get().job;
      if (!cur || cur.id !== job.id) return; // job changed/cleared mid-flight

      if (info.status === 'complete') {
        // Fetch the result BEFORE flipping status — the poller stops on
        // terminal states, so flipping first would strand a failed result
        // fetch with no retry. One atomic set keeps status ⇄ candidates
        // coherent for subscribers.
        const result = await getDetectionResult(job.siteId, job.id);
        const cur2 = get().job;
        if (!cur2 || cur2.id !== job.id) return;
        const mapped = (result.candidates ?? []).map((c, index) => ({
          id: `cand_${index}`,
          ring: c.corners,
          props: c.props,
          state: 'ghost' as const,
        }));
        // Default the sensitivity threshold to the TOP-200 view: score01 is a
        // percentile, so on large candidate sets a mid-range default shows
        // everything and the weak tail visually buries the strong head (the
        // live-demo lesson: at 50% the owner saw "confetti" and called a good
        // scan bad; at 99% the same scan read perfectly). Clamp to [0.5, 0.99].
        const scores = mapped
          .map((c) => c.props.score01 ?? 0)
          .sort((a, b) => b - a);
        const top200Floor =
          scores.length > DEFAULT_VISIBLE_GHOSTS
            ? Math.min(0.99, Math.max(0.5, scores[DEFAULT_VISIBLE_GHOSTS - 1]))
            : DEFAULT_THRESHOLD;
        set({
          job: {
            ...cur2,
            status: 'complete',
            error: undefined,
            candidateCount:
              info.candidateCount ?? (result.candidates ?? []).length,
          },
          candidates: mapped,
          summary: result.summary ?? null,
          threshold: top200Floor,
        });
      } else {
        // pending/running progress tick, or terminal error/cancelled —
        // surface job.error verbatim so the UI can show it.
        set({
          job: {
            ...cur,
            status: info.status,
            error: info.error ?? undefined,
            candidateCount: info.candidateCount ?? cur.candidateCount,
          },
        });
      }
    } catch (err) {
      if (is404(err)) {
        // Job row (or its result object) is gone — cancelled elsewhere,
        // swept, or purged. Flip to error so the poller stops.
        const cur = get().job;
        if (cur && cur.id === job.id) {
          set({
            job: {
              ...cur,
              status: 'error',
              error: (err as Error).message || 'Detection job not found',
            },
          });
        }
      } else {
        // Transient (network/5xx) — keep the job non-terminal; the next
        // poll tick retries.
        console.warn(
          '[detectionStore] refreshJob failed:',
          (err as Error).message
        );
      }
    } finally {
      set({ _refreshInFlight: false });
    }
  },

  setThreshold: (v: number) => {
    set({ threshold: Math.min(1, Math.max(0, v)) });
  },

  markAccepted: (candId: string, indicationId: string) => {
    set({
      candidates: get().candidates.map((c) =>
        c.id === candId
          ? { ...c, state: 'accepted' as const, acceptedIndicationId: indicationId }
          : c
      ),
    });
  },

  markRejected: (candId: string) => {
    set({
      candidates: get().candidates.map((c) =>
        c.id === candId
          ? { ...c, state: 'rejected' as const, acceptedIndicationId: undefined }
          : c
      ),
    });
  },

  unmarkRejected: (candId: string) => {
    set({
      candidates: get().candidates.map((c) =>
        c.id === candId && c.state === 'rejected'
          ? { ...c, state: 'ghost' as const }
          : c
      ),
    });
  },

  dismiss: async () => {
    const { job, candidates } = get();
    if (job) {
      const acceptedIds = candidates
        .filter((c) => c.state === 'accepted')
        .map((c) => c.id);
      const rejectedIds = candidates
        .filter((c) => c.state === 'rejected')
        .map((c) => c.id);
      // Best-effort labels (v2 training fuel) — a failure must not block
      // the dismiss.
      try {
        await postDetectionLabels(job.siteId, job.id, acceptedIds, rejectedIds);
      } catch (err) {
        console.warn(
          '[detectionStore] labels save failed (continuing dismiss):',
          (err as Error).message
        );
      }
      // Best-effort cancel — 404 (already swept) and transient failures
      // both fall through to the local clear; the server's watchdog/sweep
      // owns any leftover row.
      try {
        await cancelDetection(job.siteId, job.id);
      } catch (err) {
        console.warn(
          '[detectionStore] job cancel failed (clearing locally):',
          (err as Error).message
        );
      }
    }
    get().clear();
  },

  clear: () => {
    set({
      job: null,
      candidates: [],
      summary: null,
      threshold: DEFAULT_THRESHOLD,
      _refreshInFlight: false,
    });
  },
}));
